// Mounts Svelte islands on server-rendered pages. Each island is a Svelte component in
// client/islands/<name>.svelte registered below; the page marks a mount point with
// data-island="<name>" and passes props through data-* attributes (or a JSON script block
// via `{{jsonScript}}`). Islands are lazy: the component chunk loads only when the node exists.
import { mount } from 'svelte';
// Icon font: every `<i class="bi bi-…">` in the app renders nothing without it. Imported
// here rather than from the Sass theme, because Sass drops the icon class rules.
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'bootstrap/js/dist/collapse';
import 'bootstrap/js/dist/dropdown';
import 'bootstrap/js/dist/modal';
import { initReveal } from './lib/reveal.js';
import { initCardFlip } from './lib/card-flip.js';
import { initDeepLinks } from './lib/deep-links.js';
import { initRoutineOpen } from './lib/routine-open.js';
import { initPlayerCardModal } from './lib/player-card-modal.js';
import { initBulkPick } from './lib/bulk-pick.js';
import { initTableCards } from './lib/table-cards.js';
import { initDashboardGrid } from './lib/dashboard-grid.js';

const registry = {
  'season-countdown': () => import('./islands/SeasonCountdown.svelte'),
  'passkey-sign-in': () => import('./islands/PasskeySignIn.svelte'),
  'passkey-register': () => import('./islands/PasskeyRegister.svelte'),
  calendar: () => import('./islands/Calendar.svelte'),
  'event-map': () => import('./islands/EventMap.svelte'),
  'checkout-status': () => import('./islands/CheckoutStatus.svelte'),
  'evaluation-sheet': () => import('./islands/EvaluationSheet.svelte'),
  'draft-room': () => import('./islands/DraftRoom.svelte'),
  'lineup-builder': () => import('./islands/LineupBuilder.svelte'),
  'team-hierarchy': () => import('./islands/TeamHierarchy.svelte'),
  'drill-diagram': () => import('./islands/DrillDiagram.svelte'),
  'routine-builder': () => import('./islands/RoutineBuilder.svelte'),
};

export function propsFromDataset(node) {
  const props = {};
  for (const [key, value] of Object.entries(node.dataset)) {
    if (key === 'island') continue;
    props[key] = value;
  }
  const jsonId = node.dataset.props;
  if (jsonId) {
    const script = document.getElementById(jsonId);
    if (script) Object.assign(props, JSON.parse(script.textContent));
  }
  return props;
}

export async function mountIslands(root = document) {
  const nodes = root.querySelectorAll('[data-island]');
  await Promise.all(
    Array.from(nodes).map(async (node) => {
      const loader = registry[node.dataset.island];
      if (!loader) return;
      const { default: Component } = await loader();
      mount(Component, { target: node, props: propsFromDataset(node) });
      node.dataset.mounted = 'true';
    })
  );
}

if (typeof document !== 'undefined') {
  mountIslands();
  initReveal();
  initCardFlip();
  initRoutineOpen();
  initPlayerCardModal();
  initBulkPick();
  initTableCards();
  initDashboardGrid();
  initDeepLinks();
}
